import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Droplets, TrendingUp, Link2, Eye } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const SITE = "https://oceandata.ca";

const TOOLS = [
  { name: "Excel", role: "sus hojas de cálculo se quedan — se acabó el reingreso de datos" },
  { name: "QuickBooks", role: "su contabilidad se queda — conectada al trabajo real" },
  { name: "Microsoft 365", role: "sus correos y archivos se quedan — archivados en el lugar correcto" },
  { name: "Google Workspace", role: "sus agendas y documentos se quedan — sincronizados" },
  { name: "Zoho", role: "su CRM y sus proyectos se quedan — por fin conectados" },
  { name: "Autodesk", role: "sus planos se quedan — vinculados a los hitos de la obra" },
];

const LEAKS = [
  {
    title: "La reescritura",
    body: "Una cotización se vuelve proyecto, que se vuelve factura. Si un humano copia cifras de un sistema a otro, paga dos veces: en minutos, y luego en errores que reaparecen tres semanas después.",
  },
  {
    title: "La persecución",
    body: "Cuente los mensajes que solo existen para preguntar cómo va algo. Cada «¿hay novedades?» es un impuesto que pagan dos personas. Cuando el estado es visible en un solo lugar, la persecución desaparece.",
  },
  {
    title: "La búsqueda",
    body: "La última versión del presupuesto, la orden de cambio firmada, la respuesta del cliente — ¿en cuántos lugares pueden esconderse? Cada respuesta de más es arqueología en lugar de trabajo real.",
  },
  {
    title: "La reunión sobre el trabajo",
    body: "Algunas reuniones hacen avanzar el trabajo. Otras solo existen porque nadie puede verlo. La reunión del viernes desaparece en cuanto el pipeline es simplemente visible.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Muéstrenos",
    body: "Una conversación de 45 minutos sobre un solo proceso — el que más duele. Mapeamos cómo viaja realmente el trabajo, no cómo debería viajar.",
  },
  {
    n: "02",
    title: "Conectamos",
    body: "Conectamos las herramientas que ya tiene para que la información fluya sola. Su equipo practica primero en una copia segura. Nada cambia en sus hábitos.",
  },
  {
    n: "03",
    title: "Ustedes trabajan",
    body: "30 días gratis sobre sus datos reales. Al final, un informe medido: las horas devueltas a su equipo, con cifras. Usted decide después — el informe se queda con usted.",
  },
];

const FAQ = [
  {
    q: "¿Tengo que cambiar de software?",
    a: "No. Esa es toda la idea. Conectamos lo que ya usa — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk — para que la información circule sin reescritura. Nadie reaprende su trabajo.",
  },
  {
    q: "¿Cuánto tarda un resultado?",
    a: "Un primer proceso conectado se mide en semanas, no en meses. En Construction Horizon, el tiempo de respuesta a los RFI cayó 43 % en seis semanas — medido en sus propios registros.",
  },
  {
    q: "¿Por dónde se empieza, y a qué precio?",
    a: "Por una revisión gratuita de 15 minutos: usted nos muestra adónde van las horas de su equipo, nosotros le mostramos dónde simplificar. Después, un proceso a la vez, a precio fijo, con 30 días de prueba gratis antes de decidir.",
  },
];

export default function EsHerramientasPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "Mantenga sus herramientas. Las conectamos. | Ocean Data — Miami — Montréal";
    document.documentElement.lang = "es";
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = "es";
    el.href = `${SITE}/es/mantenga-sus-herramientas`;
    el.setAttribute("data-hreflang-injected", "true");
    document.head.appendChild(el);
    return () => {
      document.documentElement.lang = "en";
      el.remove();
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/es/mantenga-sus-herramientas`,
        name: "Mantenga sus herramientas. Las conectamos.",
        description:
          "Ocean Data conecta las herramientas que su empresa ya usa — Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk — en un solo sistema simple, del cliente al pago. Sin reemplazar sus programas.",
        inLanguage: "es",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "Service",
        name: "Conexión de herramientas existentes — simplificación de procesos",
        provider: { "@id": `${SITE}/#org` },
        areaServed: [
          { "@type": "City", name: "Miami" },
          { "@type": "City", name: "Montréal" },
        ],
        description:
          "Conectamos los programas que ya posee en una fuente única de verdad para la construcción y los servicios profesionales: menos papeleo, menos reescritura, menos persecución de información.",
      },
      {
        "@type": "FAQPage",
        inLanguage: "es",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="es">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Miami — Montréal · Construcción y servicios profesionales
          </p>
          <h1
            data-testid="esh-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="esh-headline-line-1">Mantenga sus herramientas.</MaskedLine>
            <MaskedLine delay={0.3} testId="esh-headline-line-2">
              <span className="text-brand-teal">Las conectamos.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Ya posee todo lo que necesita. Conectamos sus programas en un solo
            sistema simple, del cliente al pago. Menos papeleo. Menos
            repetición. Menos persecuciones.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="esh-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Muéstreme dónde simplificar
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">Revisión gratuita de 15 minutos · Sin compromiso</p>
          </motion.div>
        </div>
      </section>

      {/* El problema no son sus herramientas */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Adónde se van las horas
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-4">
            El problema no son sus herramientas. Es el espacio entre ellas.
          </h2>
          <p className="max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed mb-14">
            No reemplazamos nada. Conectamos lo que existe — y las cuatro
            fugas de abajo se cierran solas.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
            {LEAKS.map((leak, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div
                  data-testid={`esh-leak-${i}`}
                  className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-9 h-9 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center">
                      <Droplets size={16} />
                    </span>
                    <h3 className="font-display font-semibold text-lg text-brand-navy leading-snug">{leak.title}</h3>
                  </div>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{leak.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sus herramientas se quedan */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Lo que usted conserva
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Sus herramientas se quedan. El doble trabajo se va.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TOOLS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.05}>
                <div
                  data-testid={`esh-tool-${i}`}
                  className="rounded-2xl bg-white border border-slate-200 p-6 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5"
                >
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <Link2 size={15} className="text-brand-teal" />
                    <p className="font-display font-semibold text-lg text-brand-navy">{t.name}</p>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{t.role}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Cómo funciona
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Tres pasos. Un proceso a la vez.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div data-testid={`esh-step-${i}`} className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full">
                  <p className="font-mono text-sm text-brand-teal font-semibold mb-4">{s.n}</p>
                  <h3 className="font-display font-semibold text-xl text-brand-navy mb-3">{s.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Prueba */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-10">
            Resultados medidos, no promesas
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-14">
            {[
              ["647", "implementaciones de Zoho One desde 2017"],
              ["25–40 %", "menos tiempo administrativo, medido en nuestros clientes"],
              ["43 %", "respuestas a los RFI más rápidas en Construction Horizon"],
            ].map(([num, label], i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div data-testid={`esh-proof-${i}`}>
                  <p className="font-display font-bold text-4xl sm:text-5xl text-white mb-2">{num}</p>
                  <p className="text-sm text-brand-pale leading-relaxed">{label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
              <div className="flex items-start gap-4 max-w-2xl">
                <TrendingUp size={20} className="text-brand-gold shrink-0 mt-1" />
                <div>
                  <p className="font-display font-medium text-lg leading-snug text-white mb-2">
                    «En seis semanas, el tiempo de respuesta de nuestro equipo a los RFI cayó 43 %. Las decisiones ahora toman horas, no días.»
                  </p>
                  <p className="text-xs text-slate-400">Director de Operaciones, Construction Horizon</p>
                </div>
              </div>
              <Link
                to="/es/journal/horizon-construction-caso-de-estudio"
                data-testid="esh-case-study-link"
                className="shrink-0 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 text-brand-gold text-sm font-semibold px-6 py-3 transition-all duration-300 hover:bg-brand-gold hover:text-brand-navy"
              >
                <Eye size={15} /> Leer el caso de estudio
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Preguntas frecuentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Lo que nos preguntan antes de empezar
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`esh-faq-${i}`} className="group rounded-2xl bg-white border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Quince minutos. Sus herramientas. Una respuesta honesta.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Muéstrenos adónde van las horas de su equipo. Le mostramos
                  los dos o tres lugares donde simplificar primero — sin
                  reemplazar nada.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 shrink-0">
                <button
                  data-testid="esh-cta-final"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  Solicitar mi revisión gratuita
                  <ArrowRight size={18} />
                </button>
                <p className="text-xs text-slate-400">Respuesta personal en un día hábil</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
